import React, { useEffect, useState } from 'react'
import { RosterImportStatus, RosterService } from '../../services/RosterService'
import '../../styles/molecules/csv-importer/csv-uploading.scss'

interface RosterImportProgressProps {
  rosterHash: string
  fileName: string
  onComplete: (status: RosterImportStatus) => void
  onError: (error: Error) => void
}

const phaseDescriptions: Record<string, string> = {
  STARTED: 'Starting import process...',
  CREATE_UPDATE: 'Creating and updating employees...',
  ASSIGN_TEAMS: 'Assigning employees to teams...',
  CLEANUP: 'Cleaning up...',
  COMPLETE: 'Import complete!',
}

/**
 * Component to display roster import progress
 * Polls the server for import status and displays real-time progress
 */
const RosterImportProgress: React.FC<RosterImportProgressProps> = ({ rosterHash, fileName, onComplete, onError }) => {
  const [status, setStatus] = useState<RosterImportStatus | null>(null)
  const [isPolling, setIsPolling] = useState(true)

  useEffect(() => {
    if (!isPolling) return

    const statusUpdateHandler = (currentStatus: RosterImportStatus) => {
      setStatus(currentStatus)
    }

    RosterService.pollImportStatus(rosterHash, statusUpdateHandler, 0, 3000)
      .then(finalStatus => {
        setStatus(finalStatus)
        setIsPolling(false)
        onComplete(finalStatus)
      })
      .catch(error => {
        setIsPolling(false)
        onError(error)
      })
  }, [rosterHash, isPolling, onComplete, onError])

  if (!status) {
    return (
      <div className="container-csv-uploading">
        <p className="text-modal-info">
          Initializing roster import for <span>{fileName}</span>
        </p>
        <div className="progress">
          <div className="progress-bar" style={{ width: '0%' }} />
        </div>
      </div>
    )
  }

  const phaseDescription = phaseDescriptions[status.phaseName] || 'Processing...'

  return (
    <div className="container-csv-uploading">
      <p className="text-modal-info">{phaseDescription}</p>
      <div>
        <div className="progress">
          <div className="progress-bar" style={{ width: `${Math.min(status.percentComplete, 100)}%` }} />
        </div>
        <div style={{ marginTop: '16px', textAlign: 'center', fontSize: '14px' }}>
          <p>
            Progress: {status.processedCount} / {status.totalEntries} entries ({status.percentComplete.toFixed(1)}%)
          </p>
          <p style={{ fontSize: '12px', color: '#666' }}>
            Phase: {status.phaseName} ({status.currentPhase})
          </p>
          {status.lastCheckpointTime && (
            <p style={{ fontSize: '12px', color: '#666' }}>
              Last update: {new Date(status.lastCheckpointTime).toLocaleTimeString()}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default RosterImportProgress
